import React, {
    ChangeEvent,
    ComponentPropsWithRef,
    createRef,
    FocusEvent,
    forwardRef,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import cx from 'classnames';
import { normalizeOption, normalizeOptionGroup, OptionProp, OptionsGroup, Options } from '../custom-select/custom-select-input';
import { TypeaheadFlosField, type FlosFieldProps } from '../../flos-field';
import { FlosInput, FlosInputProps } from '../../input/flos-input';
import { SuggestionsList } from './typeahead-suggestion-list';

type TypeaheadFieldProps = {
    /**
     * options that will be rendered as children, you can also construct your option markup as children
     */
    options: Options;
    value?: string;
} & Omit<FlosInputProps, 'placeholder' | 'onChange'> &
    Omit<FlosFieldProps, 'onChange'> &
    Omit<ComponentPropsWithRef<'input'>, 'onBlur' | 'placeholder'>;

export const TypeaheadField = forwardRef<HTMLInputElement, Omit<TypeaheadFieldProps, 'maxLength'>>(
    (
        { value, isValid, onValidityChange, options: originalOptions, label, iconShape, errorText, helpText, onFocus, onChange, ...props },
        forwardedRef: RefObject<HTMLInputElement>
    ) => {
        const internalOptions = useMemo(() => normalizeOptionGroup(originalOptions.map(normalizeOption)), [originalOptions]);
        const [options, setOptions] = useState<OptionsGroup>(internalOptions);

        const inputRef = createRef<HTMLInputElement>();
        useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement, [inputRef]);
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;

        const wrapperRef = useRef() as RefObject<HTMLDivElement>;
        const flosInputRef = useRef() as RefObject<HTMLInputElement>;
        const [showOptions, setShowOptions] = useState<boolean>(false);
        const [internalValue, setInternalValue] = useState<string>('');
        const [isValidLocal, setIsValidLocal] = useState<boolean | undefined>(isValid);
        const [isDirtyLocal, setIsDirtyLocal] = useState<boolean | undefined>(undefined);

        const filterOptions = useCallback(
            (value: string): OptionsGroup => {
                const newOptionGroups: OptionsGroup = new Map();
                if (!value) return options;
                const isEqual = (o: OptionProp) => o.label.toLowerCase().includes(value.toLowerCase()) || o.value.toLowerCase().includes(value.toLowerCase());
                internalOptions.forEach((option: OptionProp, key) => {
                    if (Array.isArray(option)) {
                        const newOptions = option.filter(isEqual);
                        if (newOptions.length) {
                            if (option.optgroup !== undefined) {
                                newOptionGroups.set(option.optgroup, newOptions);
                            } else {
                                newOptionGroups.set(key, newOptions);
                            }
                        }
                    } else {
                        if (isEqual(option)) newOptionGroups.set(key, option);
                    }
                });
                return newOptionGroups;
            },
            [internalOptions, options]
        );

        const findOption = useCallback(
            (reducer: (o: OptionProp) => boolean): OptionProp | undefined => {
                let foundOption: OptionProp | undefined;
                for (const option of internalOptions.values()) {
                    if (Array.isArray(option)) {
                        foundOption = option.find(reducer);
                    } else if (reducer(option)) {
                        foundOption = option;
                    }
                    if (foundOption) break;
                }
                return foundOption;
            },
            [internalOptions]
        );

        const arrowUpDownAction = useCallback(
            (event: globalThis.KeyboardEvent, action: string) => {
                setShowOptions(true);
                // Gets all available options
                const suggestions = wrapperRef.current?.getElementsByClassName('typeaheadfield-suggestionlist-option-key');
                if (!suggestions || suggestions.length === 0) return;
                let activeIndex = -1;
                [...suggestions].forEach((elem, index) => {
                    if (elem === document.activeElement) {
                        activeIndex = index;
                    }
                });

                if (action === 'Home') {
                    if (activeIndex === -1) {
                        return;
                    } else {
                        activeIndex = 0;
                    }
                }

                if (action === 'End') {
                    if (activeIndex === -1) {
                        return;
                    } else {
                        activeIndex = suggestions.length - 1;
                    }
                }

                if (action === 'ArrowUp') {
                    event.preventDefault();
                    if (activeIndex - 1 < 0) {
                        flosInputRef.current?.focus();
                        return;
                    } else {
                        activeIndex--;
                    }
                }

                if (action === 'ArrowDown') {
                    event.preventDefault();
                    if (activeIndex === -1) {
                        activeIndex = 0;
                    } else {
                        if (!(activeIndex + 1 > suggestions.length - 1)) {
                            activeIndex++;
                        }
                    }
                }
                const currentActiveItem = suggestions[activeIndex];
                (currentActiveItem as HTMLElement).focus();
            },
            [flosInputRef]
        );

        const onResetHandler = () => {
            flosInputRef.current?.focus();
            setInternalValue('');
            setOptions(internalOptions);
            setIsValidLocal(undefined);
            setShowOptions(true);
            if (inputRef.current) {
                nativeInputValueSetter?.call(inputRef.current, '');
                inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
            }
        };

        const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value;
            setShowOptions(true);
            setInternalValue(newValue);
            if (newValue !== '') {
                setOptions(filterOptions(newValue));
            } else {
                setOptions(internalOptions);
            }
        };

        const onChangeHiddenHandler = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                const newValue = event.target.value;
                const extractedOption = findOption((o) => o.value === newValue);
                if (extractedOption && extractedOption.label.toLocaleLowerCase() === internalValue.toLocaleLowerCase()) {
                    setInternalValue(internalValue || extractedOption.label);
                }
                if (onChange) onChange(event);
            },
            [onChange, findOption, internalValue]
        );

        const onClickHandler = useCallback(
            (option: OptionProp) => {
                setInternalValue(option.label);
                setOptions(internalOptions);
                setIsValidLocal(true);
                setIsDirtyLocal(true);
                setShowOptions(false);
                if (inputRef.current) {
                    nativeInputValueSetter?.call(inputRef.current, option.value);
                    inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
                }
            },
            [inputRef, internalOptions, nativeInputValueSetter]
        );

        const onFocusHandler = useCallback(
            (event: FocusEvent<HTMLInputElement>) => {
                const newValue = event.currentTarget.value;
                setShowOptions(true);
                setOptions(filterOptions(newValue));
                onFocus && onFocus(event);
            },
            [filterOptions, onFocus]
        );

        const onBlurHandler = useCallback(
            (event: FocusEvent<HTMLInputElement>) => {
                const displayedValue = event.target.value || '';
                const valueToValidate = inputRef.current?.value || '';

                const extractedOption = findOption((o) => {
                    const matchedLabelOrValue =
                        o.value.toLocaleLowerCase() === displayedValue.toLocaleLowerCase() || o.label.toLocaleLowerCase() === displayedValue.toLocaleLowerCase();
                    // if value not set, checked based on label
                    if (valueToValidate && valueToValidate === o.value) return o.label === displayedValue || matchedLabelOrValue;
                    // else checked based on value
                    return o.value === valueToValidate || matchedLabelOrValue;
                });

                const isValid = !!extractedOption;
                if (displayedValue === '') {
                    setIsValidLocal(undefined);
                    setIsDirtyLocal(false);
                } else {
                    setIsValidLocal(isValid);
                    setIsDirtyLocal(true);
                }
            },
            [inputRef, findOption]
        );

        useEffect(() => {
            const keyListener = (event: KeyboardEvent) => {
                const key = event.key;
                if (key === 'ArrowUp' || key === 'ArrowDown' || key === 'Home' || key === 'End') {
                    arrowUpDownAction(event, key);
                }
                if (key === 'Escape') {
                    flosInputRef.current?.focus();
                    setShowOptions(false);
                }
                if (key === 'Tab') {
                    setShowOptions(false);
                }
                if ((key === 'Enter' || event.code === 'Space') && showOptions) {
                    const value = (document.activeElement as HTMLElement).textContent;
                    if (!value) return;
                    const targetOption = findOption((o) => o.label === value);
                    targetOption && onClickHandler(targetOption);
                }
            };

            const onClickOutsideHandler = (event: MouseEvent) => {
                if (wrapperRef && !wrapperRef.current?.contains(event.target as Node)) {
                    setShowOptions(false);
                    setOptions(internalOptions);
                }
            };

            const wrapperRefLocalCopy = wrapperRef.current;
            window.addEventListener('mousedown', onClickOutsideHandler);
            wrapperRefLocalCopy?.addEventListener('keydown', keyListener);

            return () => {
                window.removeEventListener('mousedown', onClickOutsideHandler);
                wrapperRefLocalCopy?.removeEventListener('keydown', keyListener);
            };
        }, [arrowUpDownAction, internalOptions, onClickHandler, showOptions, findOption]);

        useEffect(() => {
            setIsValidLocal(isValid);
        }, [isValid]);

        useEffect(() => {
            const v = value ? (value as string) : '';
            setInternalValue(findOption((o) => o.value === v)?.label || internalValue);
            setIsDirtyLocal(v !== undefined && v !== '');
            inputRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
        }, [value]); // dont add inputRef to dep list, things will break

        return (
            <div className={cx('typeaheadfield', showOptions && 'is-open')} ref={wrapperRef}>
                <TypeaheadFlosField
                    onValidityChange={onValidityChange}
                    isValid={isValidLocal}
                    isDirty={isDirtyLocal}
                    label={label}
                    iconShape={iconShape}
                    errorText={errorText}
                    required={props.required}
                    hasDropdown={true}
                    id={props.id}
                    helpText={helpText}
                    disabled={props.disabled}
                    resetHandler={onResetHandler}
                    ref={inputRef}
                    renderInput={({ getInputProps }) => (
                        <>
                            <FlosInput
                                {...getInputProps({ ...props, onChange: onChangeHandler, onFocus: onFocusHandler, onBlur: onBlurHandler })}
                                value={findOption((o) => o.value === internalValue)?.value || internalValue}
                                autoComplete={'off'}
                                ref={flosInputRef}
                            />
                            <SuggestionsList isOpen={showOptions} onClick={onClickHandler} options={options} />
                        </>
                    )}
                />

                <input onChange={onChangeHiddenHandler} className="visuallyhidden" ref={inputRef} value={value} tabIndex={-1} />
            </div>
        );
    }
);

TypeaheadField.displayName = 'TypeaheadField';

export type { TypeaheadFieldProps };
export default TypeaheadField;
